import React, { useState, useEffect } from 'react';
import { 
  X, Search, Save, Plus, Edit2, Trash2, RotateCcw, 
  DollarSign, Package, Calculator, TrendingUp, CheckCircle, AlertCircle 
} from 'lucide-react';
import '../styles/ConsignmentPaymentForm.css';
import apiClient from '../services/api';
import { useNavigate } from 'react-router-dom';

const ConsignmentPaymentForm = ({ onClose }) => {
  
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Search state
  const [awbSearch, setAwbSearch] = useState('');
  const [consignmentData, setConsignmentData] = useState(null);
  
  // ✅ NEW: Selection list
  const [showSelectionList, setShowSelectionList] = useState(false);
  const [consignmentList, setConsignmentList] = useState([]);
  
  // ✅ FIXED: Split into saved and local payments
  const [savedPayments, setSavedPayments] = useState([]);      // Already in database
  const [localPayments, setLocalPayments] = useState([]);      // Not saved yet (pending)
  
  // Payment form state
  const [editingPaymentIndex, setEditingPaymentIndex] = useState(-1);
  const [isEditingLocal, setIsEditingLocal] = useState(false);
  const [paymentForm, setPaymentForm] = useState(getEmptyPaymentForm());
  
  // Dropdown data
  const [paymentModes, setPaymentModes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  
  // Session
  const session = getSessionData();
  
  function getSessionData() {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      const sessionData = JSON.parse(sessionStr);
      return sessionData?.session || sessionData;
    } catch (error) {
      console.error('Error parsing session:', error);
      return null;
    }
  }
  
  function getEmptyPaymentForm() {
    return {
      PaymentModeID: '',
      CustomerID: '',
      ItemID: '',
      
      // Revenue
      RevenueCurrencyID: '',
      RevenueCurrencyAmount: '',
      RevenueExchangeRate: 1,
      Amount: 0,
      TaxPer: 0,
      
      // Supplier/Cost
      SupplierID: '',
      CostCurrencyID: '',
      CostCurrencyAmount: '',
      CostExchangeRate: 1,
      CostAmount: 0,
      
      Remarks: ''
    };
  }
  
  useEffect(() => {
    loadDropdownData();
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (showSelectionList) {
          setShowSelectionList(false);
        } else if (onClose) {
          onClose();
        } else {
          navigate('/dashboard');
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, navigate, showSelectionList]);

    
  const loadDropdownData = async () => {
    try {
      const [pmRes, custRes, itemRes, suppRes, currRes] = await Promise.all([
        apiClient.get('/PaymentModes.php'),
        apiClient.get('/Customers.php'),
        apiClient.get('/Items.php'),
        apiClient.get('/Suppliers.php'),
        apiClient.get('/Currencies.php')
      ]);
      
      setPaymentModes(Array.isArray(pmRes.data?.data) ? pmRes.data.data : []);
      setCustomers(Array.isArray(custRes.data?.data) ? custRes.data.data : []);
      setItems(Array.isArray(itemRes.data?.data) ? itemRes.data.data : []);
      setSuppliers(Array.isArray(suppRes.data?.data) ? suppRes.data.data : []);
      setCurrencies(Array.isArray(currRes.data?.data) ? currRes.data.data : []);
      
    } catch (err) {
      console.error('Error loading dropdowns:', err);
      setError('Error loading dropdown data');
    }
  };
  
  // Search with partial matching
  const handleSearch = async () => {
    if (!awbSearch.trim()) {
      setError('Please enter consignment number or search term');
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const searchResponse = await apiClient.get(`/ConsignmentPayments.php?search=${awbSearch}`);
      
      if (searchResponse.data.success && searchResponse.data.count > 0) {
        const results = searchResponse.data.data;
        
        if (results.length === 1) {
          loadConsignmentDetails(results[0].ConsignmentNo);
        } else {
          setConsignmentList(results);
          setShowSelectionList(true);
          setError('');
        }
      } else {
        setError('No consignments found matching "' + awbSearch + '"');
        setConsignmentData(null);
        setSavedPayments([]);
        setLocalPayments([]);
      }
    } catch (err) {
      console.error('Error searching:', err);
      setError('Error searching consignments: ' + (err.response?.data?.message || err.message));
      setConsignmentData(null);
      setSavedPayments([]);
      setLocalPayments([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Load full consignment details
  const loadConsignmentDetails = async (consignmentNo) => {
    setLoading(true);
    setError('');
    
    try {
      const response = await apiClient.get(`/ConsignmentPayments.php?awb=${consignmentNo}`);
      
      if (response.data.success && response.data.data) {
        setConsignmentData(response.data.data);
        loadSavedPayments(response.data.data.ConsignmentID);
        setLocalPayments([]); // Clear local payments when loading new consignment
        setShowSelectionList(false);
        setError('');
      } else {
        setError('Consignment not found');
        setConsignmentData(null);
        setSavedPayments([]);
        setLocalPayments([]);
      }
    } catch (err) {
      console.error('Error loading details:', err);
      setError('Error loading consignment: ' + (err.response?.data?.message || err.message));
      setConsignmentData(null);
      setSavedPayments([]);
      setLocalPayments([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Select from list
  const handleSelectConsignment = (consignment) => {
    setAwbSearch(consignment.ConsignmentNo);
    loadConsignmentDetails(consignment.ConsignmentNo);
  };
  
  // Load saved payments from database
  const loadSavedPayments = async (consignmentId) => {
    try {
      const response = await apiClient.get(`/ConsignmentPayments.php?consignmentid=${consignmentId}`);
      
      if (response.data.success) {
        setSavedPayments(response.data.data || []);
      }
    } catch (err) {
      console.error('Error loading payments:', err);
    }
  };
  
  const handlePaymentFormChange = (e) => {
    const { name, value } = e.target;
    setPaymentForm(prev => {
      const updated = { ...prev, [name]: value };
      
      // Auto-calculate revenue amount
      if (name === 'RevenueCurrencyAmount' || name === 'RevenueExchangeRate') {
        const currAmt = parseFloat(name === 'RevenueCurrencyAmount' ? value : updated.RevenueCurrencyAmount) || 0;
        const exchRate = parseFloat(name === 'RevenueExchangeRate' ? value : updated.RevenueExchangeRate) || 1;
        updated.Amount = (currAmt * exchRate).toFixed(2);
      }
      
      // Auto-calculate cost amount
      if (name === 'CostCurrencyAmount' || name === 'CostExchangeRate') {
        const costAmt = parseFloat(name === 'CostCurrencyAmount' ? value : updated.CostCurrencyAmount) || 0;
        const costRate = parseFloat(name === 'CostExchangeRate' ? value : updated.CostExchangeRate) || 1;
        updated.CostAmount = (costAmt * costRate).toFixed(2);
      }
      
      return updated;
    });
  };
  
  // ✅ FIXED: Add payment to LOCAL list (not database)
  const handleAddPayment = () => {
    if (!consignmentData) {
      setError('No consignment loaded');
      return;
    }
    
    if (!paymentForm.PaymentModeID) {
      setError('Please select payment mode');
      return;
    }
    
    // Calculate final values
    const revenueCurrencyAmount = parseFloat(paymentForm.RevenueCurrencyAmount) || 0;
    const revenueExchangeRate = parseFloat(paymentForm.RevenueExchangeRate) || 1;
    const amount = revenueCurrencyAmount * revenueExchangeRate;
    
    const taxPer = parseFloat(paymentForm.TaxPer) || 0;
    const taxAmt = amount * (taxPer / 100);
    const totalAmt = amount + taxAmt;
    
    const costCurrencyAmount = parseFloat(paymentForm.CostCurrencyAmount) || 0;
    const costExchangeRate = parseFloat(paymentForm.CostExchangeRate) || 1;
    const costAmount = costCurrencyAmount * costExchangeRate;
    
    // Get names for display
    const paymentMode = paymentModes.find(pm => pm.paymentmodeid == paymentForm.PaymentModeID);
    const customer = customers.find(c => c.customerid == paymentForm.CustomerID);
    const item = items.find(i => i.itemid == paymentForm.ItemID);
    const supplier = suppliers.find(s => s.supplierid == paymentForm.SupplierID);
    const revCurr = currencies.find(c => c.currencyid == paymentForm.RevenueCurrencyID);
    const costCurr = currencies.find(c => c.currencyid == paymentForm.CostCurrencyID);
    
    const newPayment = {
      ...paymentForm,
      Amount: amount.toFixed(2),
      TaxAmt: taxAmt.toFixed(2),
      TotalAmt: totalAmt.toFixed(2),
      CostAmount: costAmount.toFixed(2),
      
      // Display names
      PaymentMode: paymentMode?.paymentmodename || '',
      PaymentModeCode: paymentMode?.paymentmodecode || '',
      CustomerName: customer?.customername || '',
      ItemName: item?.itemname || '',
      SupplierName: supplier?.suppliername || '',
      RevenueCurrency: revCurr?.symbol || '',
      CostCurrency: costCurr?.symbol || ''
    };
    
    if (editingPaymentIndex >= 0 && isEditingLocal) {
      // Update existing local payment
      const updated = [...localPayments];
      updated[editingPaymentIndex] = newPayment;
      setLocalPayments(updated);
      setSuccess('Payment updated in list!');
    } else {
      // Add new local payment
      setLocalPayments([...localPayments, newPayment]);
      setSuccess('Payment added to list! Click "Save (F5)" to save all payments to database.');
    }
    
    // Reset form
    setPaymentForm(getEmptyPaymentForm());
    setEditingPaymentIndex(-1);
    setIsEditingLocal(false);
    setError('');
  };
  
  // ✅ NEW: Edit local payment
  const handleEditLocalPayment = (index) => {
    const payment = localPayments[index];
    setPaymentForm({
      PaymentModeID: payment.PaymentModeID || '',
      CustomerID: payment.CustomerID || '',
      ItemID: payment.ItemID || '',
      RevenueCurrencyID: payment.RevenueCurrencyID || '',
      RevenueCurrencyAmount: payment.RevenueCurrencyAmount || '',
      RevenueExchangeRate: payment.RevenueExchangeRate || 1,
      Amount: payment.Amount || 0,
      TaxPer: payment.TaxPer || 0,
      SupplierID: payment.SupplierID || '',
      CostCurrencyID: payment.CostCurrencyID || '',
      CostCurrencyAmount: payment.CostCurrencyAmount || '',
      CostExchangeRate: payment.CostExchangeRate || 1,
      CostAmount: payment.CostAmount || 0,
      Remarks: payment.Remarks || ''
    });
    setEditingPaymentIndex(index);
    setIsEditingLocal(true);
    setError('');
    setSuccess('');
  };
  
  // ✅ NEW: Delete local payment
  const handleDeleteLocalPayment = (index) => {
    if (!window.confirm('Remove this payment from the list?')) {
      return;
    }
    
    const updated = localPayments.filter((_, i) => i !== index);
    setLocalPayments(updated);
    setSuccess('Payment removed from list');
  };
  
  // ✅ FIXED: Save ALL local payments to database at once
  const handleSaveAll = async () => {
    if (!consignmentData) {
      setError('No consignment loaded');
      return;
    }
    
    if (localPayments.length === 0) {
      setError('No payments to save. Add payments first.');
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      let successCount = 0;
      let errorCount = 0;
      
      // Save each local payment to database
      for (const payment of localPayments) {
        try {
          const dataToSave = {
            ConsignmentID: consignmentData.ConsignmentID,
            PaymentModeID: payment.PaymentModeID,
            CustomerID: payment.CustomerID || null,
            ItemID: payment.ItemID || null,
            RevenueCurrencyID: payment.RevenueCurrencyID || null,
            RevenueCurrencyAmount: payment.RevenueCurrencyAmount,
            RevenueExchangeRate: payment.RevenueExchangeRate,
            TaxPer: payment.TaxPer,
            SupplierID: payment.SupplierID || null,
            CostCurrencyID: payment.CostCurrencyID || null,
            CostCurrencyAmount: payment.CostCurrencyAmount,
            CostExchangeRate: payment.CostExchangeRate,
            Remarks: payment.Remarks
          };
          
          await apiClient.post('/ConsignmentPayments.php', dataToSave);
          successCount++;
        } catch (err) {
          console.error('Error saving payment:', err);
          errorCount++;
        }
      }
      
      if (successCount > 0) {
        setSuccess(`Successfully saved ${successCount} payment(s) to database!`);
        setLocalPayments([]); // Clear local list
        loadSavedPayments(consignmentData.ConsignmentID); // Reload from database
        loadConsignmentDetails(consignmentData.ConsignmentNo); // Refresh summary
      }
      
      if (errorCount > 0) {
        setError(`Failed to save ${errorCount} payment(s). Check console for details.`);
      }
      
    } catch (err) {
      console.error('Error saving payments:', err);
      setError('Error saving payments: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };
  
  const handleCancelEdit = () => {
    setPaymentForm(getEmptyPaymentForm());
    setEditingPaymentIndex(-1);
    setIsEditingLocal(false);
    setError('');
    setSuccess('');
  };
  
  const handleClearAll = () => {
    setAwbSearch('');
    setConsignmentData(null);
    setSavedPayments([]);
    setLocalPayments([]);
    setPaymentForm(getEmptyPaymentForm());
    setEditingPaymentIndex(-1);
    setIsEditingLocal(false);
    setError('');
    setSuccess('');
    setShowSelectionList(false);
    setConsignmentList([]);
  };
  
  // Calculate totals (saved + local)
  const calculateTotals = () => {
    const savedTotal = savedPayments.reduce((sum, p) => sum + (parseFloat(p.TotalAmt) || 0), 0);
    const savedCost = savedPayments.reduce((sum, p) => sum + (parseFloat(p.CostAmount) || 0), 0);
    
    const localTotal = localPayments.reduce((sum, p) => sum + (parseFloat(p.TotalAmt) || 0), 0);
    const localCost = localPayments.reduce((sum, p) => sum + (parseFloat(p.CostAmount) || 0), 0);
    
    const totalRevenue = savedTotal + localTotal;
    const totalCost = savedCost + localCost;
    const profitLoss = totalRevenue - totalCost;
    
    return { totalRevenue, totalCost, profitLoss };
  };
  
  const totals = calculateTotals();
  
  // Calculate form totals
  const formTaxAmt = ((parseFloat(paymentForm.Amount) || 0) * (parseFloat(paymentForm.TaxPer) || 0) / 100).toFixed(2);
  const formTotalAmt = ((parseFloat(paymentForm.Amount) || 0) * (1 + (parseFloat(paymentForm.TaxPer) || 0) / 100)).toFixed(2);
  const formProfit = (parseFloat(formTotalAmt) - (parseFloat(paymentForm.CostAmount) || 0)).toFixed(2);
  
  if (!session?.companyid) {
    return (
     <div 
        className="payment-form-overlay"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            if (onClose) {
              onClose();
            } else {
              navigate('/dashboard');
            }
          }
        }}
      >
       <div className="payment-form-container" style={{ maxWidth: '600px' }} onClick={(e) => e.stopPropagation()}>
        <div className="form-header">
          <h2>Session Required</h2>
          <button 
            className="close-btn-fixed"
            onClick={() => {
              if (onClose) {
                onClose();
              } else {
                navigate('/dashboard');
              }
            }}
            type="button"
            title="Close (ESC)"
          >
            <X size={20} />
          </button>
        </div>
        <div style={{ padding: '24px', textAlign: 'center' }}>
          <p>Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    </div>
    );
  }
  
  return (
    <div 
      className="payment-form-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          if (onClose) {
            onClose();
          } else {
            navigate('/dashboard');
          }
        }
      }}
    >
      <div className="payment-form-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="form-header">
          <div className="header-left">
            <DollarSign size={24} />
            <h2>Consignment Payment Entry</h2>
          </div>
          <button 
            className="close-btn-fixed"
            onClick={() => {
              if (onClose) {
                onClose();
              } else {
                navigate('/dashboard');
              }
            }}
            type="button"
            title="Close (ESC)"
          >
            <X size={20} />
          </button>
        </div>
        
        {/* Toolbar */}
        <div className="payment-toolbar">
          <button onClick={handleSearch} disabled={loading} className="btn-toolbar">
            <Search size={18} />
            Search (F6)
          </button>
          <button 
            onClick={handleSaveAll} 
            disabled={loading || localPayments.length === 0} 
            className="btn-toolbar btn-save"
          >
            <Save size={18} />
            Save All ({localPayments.length}) (F5)
          </button>
          <button onClick={handleClearAll} disabled={loading} className="btn-toolbar">
            <RotateCcw size={18} />
            Clear All (Esc)
          </button>
        </div>
        
        {/* Alerts */}
        {error && (
          <div className="alert alert-error">
            <strong>Error:</strong> {error}
          </div>
        )}
        
        {success && (
          <div className="alert alert-success">
            <strong>Success:</strong> {success}
          </div>
        )}
        
        {/* Search Section */}
        <div className="payment-search-section">
          <label>AWB / Consignment No (Type partial number):</label>
          <input
            type="text"
            value={awbSearch}
            onChange={(e) => setAwbSearch(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Type 1, 2, or any part of consignment number"
            disabled={loading}
            autoFocus
          />
          <button onClick={handleSearch} disabled={loading} className="btn-search-inline">
            <Search size={18} />
          </button>
        </div>
        
        <div className="form-content">
          {/* Selection List Modal */}
          {showSelectionList && (
            <div className="selection-modal" onClick={() => setShowSelectionList(false)}>
              <div className="selection-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="selection-modal-header">
                  <h3>
                    <Package size={20} />
                    Select Consignment ({consignmentList.length} found)
                  </h3>
                  <button onClick={() => setShowSelectionList(false)} className="btn-close-modal">
                    <X size={20} />
                  </button>
                </div>
                
                <div className="selection-list">
                  <table className="selection-table">
                    <thead>
                      <tr>
                        <th>Consignment No</th>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Shipper</th>
                        <th>Consignee</th>
                        <th>Pcs</th>
                        <th>Weight</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {consignmentList.map((cons) => (
                        <tr key={cons.ConsignmentID}>
                          <td className="cons-no">{cons.ConsignmentNo}</td>
                          <td>{new Date(cons.ConsignmentDate).toLocaleDateString()}</td>
                          <td>
                            <span className="type-badge">{cons.ConsignmentType || 'LT'}</span>
                          </td>
                          <td>{cons.ShipperName || '-'}</td>
                          <td>{cons.ConsigneeName || '-'}</td>
                          <td className="text-right">{cons.Pieces || 0}</td>
                          <td className="text-right">{cons.Weight || 0} kg</td>
                          <td>
                            <button 
                              onClick={() => handleSelectConsignment(cons)}
                              className="btn-select"
                            >
                              <CheckCircle size={16} />
                              Select
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
          
          {/* Consignment Details */}
          {consignmentData && (
            <div className="form-section">
              <h3 className="section-title">
                <Package size={18} />
                Consignment Details
              </h3>
              <div className="details-grid">
                <div className="detail-item">
                  <label>Consignment No:</label>
                  <div className="detail-value">{consignmentData.ConsignmentNo}</div>
                </div>
                <div className="detail-item">
                  <label>Date:</label>
                  <div className="detail-value">
                    {new Date(consignmentData.ConsignmentDate).toLocaleDateString()}
                  </div>
                </div>
                <div className="detail-item">
                  <label>Shipper:</label>
                  <div className="detail-value">{consignmentData.ShipperName}</div>
                </div>
                <div className="detail-item">
                  <label>Consignee:</label>
                  <div className="detail-value">{consignmentData.ConsigneeName}</div>
                </div>
                <div className="detail-item">
                  <label>Origin:</label>
                  <div className="detail-value">{consignmentData.OriginCountry}</div>
                </div>
                <div className="detail-item">
                  <label>Destination:</label>
                  <div className="detail-value">{consignmentData.DestinationCountry}</div>
                </div>
                <div className="detail-item">
                  <label>Pieces:</label>
                  <div className="detail-value">{consignmentData.Pieces}</div>
                </div>
                <div className="detail-item">
                  <label>Weight:</label>
                  <div className="detail-value">{consignmentData.StatedWeight} kg</div>
                </div>
              </div>
              
              {/* Payment Summary */}
              <div className="payment-summary-compact">
                <div className="summary-item-compact">
                  <span className="label">Saved:</span>
                  <span className="value">{savedPayments.length}</span>
                </div>
                <div className="summary-item-compact">
                  <span className="label">Pending:</span>
                  <span className="value" style={{color: '#f59e0b'}}>{localPayments.length}</span>
                </div>
                <div className="summary-item-compact">
                  <span className="label">Revenue:</span>
                  <span className="value revenue">
                    {totals.totalRevenue.toFixed(2)}
                  </span>
                </div>
                <div className="summary-item-compact">
                  <span className="label">Cost:</span>
                  <span className="value cost">
                    {totals.totalCost.toFixed(2)}
                  </span>
                </div>
                <div className="summary-item-compact">
                  <span className="label">P/L:</span>
                  <span className={`value ${totals.profitLoss >= 0 ? 'profit' : 'loss'}`}>
                    {totals.profitLoss.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          )}
          
          {/* Payment Form */}
          {consignmentData && (
            <div className="form-section">
              <h3 className="section-title">
                <Calculator size={18} />
                {editingPaymentIndex >= 0 ? 'Edit Payment Entry' : 'Add Payment Entry'}
              </h3>
              
              <div className="form-grid">
                {/* Payment Mode and Basic Info */}
                <div className="form-group">
                  <label>Payment Mode *</label>
                  <select
                    name="PaymentModeID"
                    value={paymentForm.PaymentModeID}
                    onChange={handlePaymentFormChange}
                    required
                  >
                    <option value="">Select Payment Mode</option>
                    {paymentModes.map(pm => (
                      <option key={pm.paymentmodeid} value={pm.paymentmodeid}>
                        {pm.paymentmodename} ({pm.paymentmodecode})
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Customer</label>
                  <select
                    name="CustomerID"
                    value={paymentForm.CustomerID}
                    onChange={handlePaymentFormChange}
                  >
                    <option value="">Select Customer</option>
                    {customers.map(c => (
                      <option key={c.customerid} value={c.customerid}>
                        {c.customername}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Item</label>
                  <select
                    name="ItemID"
                    value={paymentForm.ItemID}
                    onChange={handlePaymentFormChange}
                  >
                    <option value="">Select Item</option>
                    {items.map(item => (
                      <option key={item.itemid} value={item.itemid}>
                        {item.itemname}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              {/* Revenue Section */}
              <div className="subsection">
                <h4 className="subsection-title">Revenue Details</h4>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Revenue Currency</label>
                    <select
                      name="RevenueCurrencyID"
                      value={paymentForm.RevenueCurrencyID}
                      onChange={handlePaymentFormChange}
                    >
                      <option value="">Select Currency</option>
                      {Array.isArray(currencies) && currencies.map(curr => (
                        <option key={curr.currencyid} value={curr.currencyid}>
                          {curr.symbol} - {curr.currencyname}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label>Revenue Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      name="RevenueCurrencyAmount"
                      value={paymentForm.RevenueCurrencyAmount}
                      onChange={handlePaymentFormChange}
                      placeholder="0.00"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Exchange Rate</label>
                    <input
                      type="number"
                      step="0.0001"
                      name="RevenueExchangeRate"
                      value={paymentForm.RevenueExchangeRate}
                      onChange={handlePaymentFormChange}
                      placeholder="1.0000"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Base Amount (Auto)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={paymentForm.Amount}
                      readOnly
                      className="readonly-field"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Tax %</label>
                    <input
                      type="number"
                      step="0.01"
                      name="TaxPer"
                      value={paymentForm.TaxPer}
                      onChange={handlePaymentFormChange}
                      placeholder="0.00"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Tax Amount (Auto)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formTaxAmt}
                      readOnly
                      className="readonly-field"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Total Revenue (Auto)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formTotalAmt}
                      readOnly
                      className="readonly-field highlight"
                    />
                  </div>
                </div>
              </div>
              
              {/* Cost Section */}
              <div className="subsection">
                <h4 className="subsection-title">Cost Details</h4>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Supplier</label>
                    <select
                      name="SupplierID"
                      value={paymentForm.SupplierID}
                      onChange={handlePaymentFormChange}
                    >
                      <option value="">Select Supplier</option>
                      {suppliers.map(s => (
                        <option key={s.supplierid} value={s.supplierid}>
                          {s.suppliername}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label>Cost Currency</label>
                    <select
                      name="CostCurrencyID"
                      value={paymentForm.CostCurrencyID}
                      onChange={handlePaymentFormChange}
                    >
                      <option value="">Select Currency</option>
                      {Array.isArray(currencies) && currencies.map(curr => (
                        <option key={curr.currencyid} value={curr.currencyid}>
                          {curr.symbol} - {curr.currencyname}
                        </option>
                      ))}
                    </select>
                  </div> 
                  
                  <div className="form-group">
                    <label>Cost Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      name="CostCurrencyAmount"
                      value={paymentForm.CostCurrencyAmount}
                      onChange={handlePaymentFormChange}
                      placeholder="0.00"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Cost Exchange Rate</label>
                    <input
                      type="number"
                      step="0.0001"
                      name="CostExchangeRate"
                      value={paymentForm.CostExchangeRate}
                      onChange={handlePaymentFormChange}
                      placeholder="1.0000"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Base Cost (Auto)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={paymentForm.CostAmount}
                      readOnly
                      className="readonly-field"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Profit/Loss (Auto)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formProfit}
                      readOnly
                      className={`readonly-field ${parseFloat(formProfit) >= 0 ? 'highlight-profit' : 'highlight-loss'}`}
                    />
                  </div>
                </div>
              </div>
              
              {/* Remarks */}
              <div className="form-group full-width">
                <label>Remarks</label>
                <textarea
                  name="Remarks"
                  value={paymentForm.Remarks}
                  onChange={handlePaymentFormChange}
                  rows="2"
                  placeholder="Payment remarks"
                />
              </div>
              
              {/* Form Actions */}
              <div className="form-actions">
                <button onClick={handleAddPayment} className="btn-primary" disabled={loading}>
                  {editingPaymentIndex >= 0 ? (
                    <>
                      <Save size={18} />
                      Update Payment in List
                    </>
                  ) : (
                    <>
                      <Plus size={18} />
                      Add Payment to List
                    </>
                  )}
                </button>
                {editingPaymentIndex >= 0 && (
                  <button onClick={handleCancelEdit} className="btn-secondary">
                    Cancel Edit
                  </button>
                )}
              </div>
            </div>
          )}
          
          {/* Payment List */}
          {consignmentData && (savedPayments.length > 0 || localPayments.length > 0) && (
            <div className="form-section">
              <h3 className="section-title">
                <TrendingUp size={18} />
                Payment Entries ({savedPayments.length} saved, {localPayments.length} pending)
              </h3>
              
              <div className="payment-table-container">
                <table className="payment-table">
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Payment Mode</th>
                      <th>Customer</th>
                      <th>Item</th>
                      <th className="text-right">Revenue</th>
                      <th className="text-right">Tax</th>
                      <th className="text-right">Total</th>
                      <th className="text-right">Cost</th>
                      <th className="text-right">Profit/Loss</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Saved Payments */}
                    {savedPayments.map((payment) => (
                      <tr key={`saved-${payment.ConsignmentPaymentID}`} className="saved-payment-row">
                        <td>
                          <span className="status-badge status-saved">
                            <CheckCircle size={14} />
                            Saved
                          </span>
                        </td>
                        <td>{payment.PaymentMode}</td>
                        <td>{payment.CustomerName || '-'}</td>
                        <td>{payment.ItemName || '-'}</td>
                        <td className="text-right">
                          {parseFloat(payment.Amount || 0).toFixed(2)}
                        </td>
                        <td className="text-right">
                          {parseFloat(payment.TaxAmt || 0).toFixed(2)}
                        </td>
                        <td className="text-right font-weight-bold">
                          {parseFloat(payment.TotalAmt || 0).toFixed(2)}
                        </td>
                        <td className="text-right">
                          {parseFloat(payment.CostAmount || 0).toFixed(2)}
                        </td>
                        <td className={`text-right font-weight-bold ${
                          (parseFloat(payment.TotalAmt || 0) - parseFloat(payment.CostAmount || 0)) >= 0 
                            ? 'text-profit' 
                            : 'text-loss'
                        }`}>
                          {(parseFloat(payment.TotalAmt || 0) - parseFloat(payment.CostAmount || 0)).toFixed(2)}
                        </td>
                        <td className="actions-cell">
                          <span style={{color: '#10b981', fontSize: '12px'}}>In Database</span>
                        </td>
                      </tr>
                    ))}
                    
                    {/* Local Payments (Pending) */}
                    {localPayments.map((payment, index) => (
                      <tr key={`local-${index}`} className="local-payment-row">
                        <td>
                          <span className="status-badge status-pending">
                            <AlertCircle size={14} />
                            Pending
                          </span>
                        </td>
                        <td>{payment.PaymentMode}</td>
                        <td>{payment.CustomerName || '-'}</td>
                        <td>{payment.ItemName || '-'}</td>
                        <td className="text-right">
                          {parseFloat(payment.Amount || 0).toFixed(2)}
                        </td>
                        <td className="text-right">
                          {parseFloat(payment.TaxAmt || 0).toFixed(2)}
                        </td>
                        <td className="text-right font-weight-bold">
                          {parseFloat(payment.TotalAmt || 0).toFixed(2)}
                        </td>
                        <td className="text-right">
                          {parseFloat(payment.CostAmount || 0).toFixed(2)}
                        </td>
                        <td className={`text-right font-weight-bold ${
                          (parseFloat(payment.TotalAmt || 0) - parseFloat(payment.CostAmount || 0)) >= 0 
                            ? 'text-profit' 
                            : 'text-loss'
                        }`}>
                          {(parseFloat(payment.TotalAmt || 0) - parseFloat(payment.CostAmount || 0)).toFixed(2)}
                        </td>
                        <td className="actions-cell">
                          <button
                            onClick={() => handleEditLocalPayment(index)}
                            className="btn-icon btn-edit"
                            title="Edit"
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                            onClick={() => handleDeleteLocalPayment(index)}
                            className="btn-icon btn-delete"
                            title="Delete"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="totals-row">
                      <td colSpan="6" className="text-right font-weight-bold">Totals:</td>
                      <td className="text-right font-weight-bold">
                        {totals.totalRevenue.toFixed(2)}
                      </td>
                      <td className="text-right font-weight-bold">
                        {totals.totalCost.toFixed(2)}
                      </td>
                      <td className={`text-right font-weight-bold ${
                        totals.profitLoss >= 0 ? 'text-profit' : 'text-loss'
                      }`}>
                        {totals.profitLoss.toFixed(2)}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              
              {localPayments.length > 0 && (
                <div className="save-reminder">
                  <AlertCircle size={18} />
                  <span>
                    You have {localPayments.length} unsaved payment(s). Click <strong>"Save All (F5)"</strong> button above to save to database.
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConsignmentPaymentForm;